import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../../../store';
import { fetchSamTypes, fetchDataDefinitions, FormatProfile, SamType, DataDefinitionColumn } from '../../../../store/dataDefinitionsSlice';
import { be1Fetch } from '../../../../config/api';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import { showAlert, showConfirm } from '../../../common/AppDialog';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../../../services/errorText';
import { useDefinitionPropagation } from './useDefinitionPropagation';
import store from '../../../../store';

interface Props { onClose: () => void }

const emptyProfile: Partial<FormatProfile> = { name: '', format: 'delimited', delimiter: ',', hasHeader: true, encoding: 'UTF-8', description: '' };
const emptyType: Partial<SamType> = { name: '', profileId: '', columns: [], fieldWidths: '', pathPattern: '', description: '' };

/**
 * SAM유형 관리 — 두 계층으로 형식을 유형화한다.
 *   커스텀구분유형(형식 프로파일): 형식·구분자·헤더·캐릭터셋만 (예: "CSV포맷")
 *   SAM유형: 프로파일 + 항목 레이아웃(+고정폭 길이, 경로 패턴) (예: "일별 주문파일")
 * SAM유형을 고치면 그 유형을 참조하는 데이터 정의가 서버에서 한꺼번에 갱신된다.
 */
const SamTypeManagerDialog: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const dispatch = useDispatch<AppDispatch>();
  const profiles = useSelector((s: RootState) => s.dataDefinitions.samProfiles);
  const types = useSelector((s: RootState) => s.dataDefinitions.samTypes);
  const definitions = useSelector((s: RootState) => s.dataDefinitions.items);
  const [editProfile, setEditProfile] = useState<Partial<FormatProfile> | null>(null);
  const [editType, setEditType] = useState<Partial<SamType> | null>(null);
  const [busy, setBusy] = useState(false);
  const propagate = useDefinitionPropagation();

  useEffect(() => { dispatch(fetchSamTypes()); }, [dispatch]);
  const refCount = (typeId: string) => definitions.filter((d) => (d.source as any)?.samTypeId === typeId).length;
  const profName = (id: string) => profiles.find((p) => p.id === id)?.name || '?';

  const call = async (path: string, method: string, body?: any) => {
    setBusy(true);
    try {
      const r = await be1Fetch(path, { method, headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(errorText(d, t('datadef:common.requestFailed')));
      await dispatch(fetchSamTypes());
      await dispatch(fetchDataDefinitions());   // 전파된 정의 갱신 반영
      // 서버가 SAM유형 → 정의로 퍼뜨렸다. 한 단계 더 — 그 정의를 쓰는 노드까지 퍼뜨린다(정의 → 노드).
      // 바뀌지 않은 노드는 건드리지 않으므로 유형을 쓰는 정의 전부를 넘겨도 된다.
      const typed = store.getState().dataDefinitions.items.filter((d) => (d.source as any)?.samTypeId).map((d) => d.id);
      await propagate(typed);
      return true;
    } catch (e) { showAlert((e as Error).message); return false; } finally { setBusy(false); }
  };

  const inp = 'border border-gray-300 rounded px-2 py-1 text-sm bg-white';
  const fmtOf = (p: FormatProfile) =>
    `${p.format === 'fixed' ? t('datadef:sam.fixed') : t('datadef:sam.delimiterIs', { d: p.delimiter === String.fromCharCode(9) ? 'TAB' : p.delimiter })} · ${p.hasHeader ? t('datadef:sam.headerYes') : t('datadef:sam.headerNo')} · ${p.encoding}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-[900px] max-w-[95vw] h-[76vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('datadef:sam.title')}</div>
            <div className="text-[0.6875rem] text-gray-500">{t('datadef:sam.subtitle')}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('datadef:common.closeEsc')}>✕</button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-2 divide-x divide-gray-200">
          {/* 커스텀구분유형 */}
          <div className="flex flex-col min-h-0">
            <div className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-50 border-b border-gray-200 flex items-center">
              {t('datadef:sam.profiles')}
              <button className="ml-auto px-2 py-0.5 text-[0.6875rem] rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
                onClick={() => setEditProfile({ ...emptyProfile })}>{t('datadef:sam.newType')}</button>
            </div>
            <div className="flex-1 overflow-auto">
              {profiles.length === 0 && <div className="p-3 text-xs text-gray-400">{t('datadef:sam.noProfiles')}</div>}
              {profiles.map((p) => (
                <div key={p.id} className="px-3 py-1.5 border-b border-gray-100 flex items-center gap-2 text-sm hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-800 truncate">{p.name}</div>
                    <div className="text-[0.6875rem] text-gray-500">{fmtOf(p)}</div>
                  </div>
                  <button className="px-1.5 py-0.5 text-[0.6875rem] rounded border border-gray-300 hover:bg-gray-100" onClick={() => setEditProfile({ ...p })}>{t('common:ui.edit')}</button>
                  <button className="px-1.5 py-0.5 text-[0.6875rem] rounded border border-red-200 text-red-600 hover:bg-red-50"
                    onClick={async () => { if (await showConfirm(t('datadef:sam.deleteProfile', { name: p.name }))) call(`/api/sam-types/profiles/${p.id}`, 'DELETE'); }}>{t('common:ui.delete')}</button>
                </div>
              ))}
            </div>
          </div>

          {/* SAM유형 */}
          <div className="flex flex-col min-h-0">
            <div className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-50 border-b border-gray-200 flex items-center">
              {t('datadef:sam.types')}
              <button className="ml-auto px-2 py-0.5 text-[0.6875rem] rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 disabled:opacity-40"
                disabled={profiles.length === 0} onClick={() => setEditType({ ...emptyType, profileId: profiles[0]?.id || '' })}>{t('datadef:sam.newType')}</button>
            </div>
            <div className="flex-1 overflow-auto">
              {types.length === 0 && <div className="p-3 text-xs text-gray-400">{t('datadef:sam.noTypes')}</div>}
              {types.map((ty) => (
                <div key={ty.id} className="px-3 py-1.5 border-b border-gray-100 flex items-center gap-2 text-sm hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-800 truncate">{ty.name}
                      {refCount(ty.id) > 0 && <span className="ml-1.5 text-[0.625rem] px-1 rounded bg-blue-100 text-blue-700" title={t('datadef:sam.refCountTitle')}>{t('datadef:sam.refCount', { n: refCount(ty.id) })}</span>}
                    </div>
                    <div className="text-[0.6875rem] text-gray-500 truncate">
                      {profName(ty.profileId)}{t('datadef:sam.typeSummary', { n: ty.columns.length })}{ty.pathPattern ? t('datadef:sam.pattern', { p: ty.pathPattern }) : ''}
                    </div>
                  </div>
                  <button className="px-1.5 py-0.5 text-[0.6875rem] rounded border border-gray-300 hover:bg-gray-100" onClick={() => setEditType({ ...ty, columns: ty.columns.map((c) => ({ ...c })) })}>{t('common:ui.edit')}</button>
                  <button className="px-1.5 py-0.5 text-[0.6875rem] rounded border border-red-200 text-red-600 hover:bg-red-50"
                    onClick={async () => { if (await showConfirm(t('datadef:sam.deleteType', { name: ty.name }))) call(`/api/sam-types/types/${ty.id}`, 'DELETE'); }}>{t('common:ui.delete')}</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-4 py-2 border-t border-gray-200 flex justify-end">
          <button className="px-3 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={onClose}>{t('common:ui.close')}</button>
        </div>
      </div>

      {/* 프로파일 편집 */}
      {editProfile && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={() => setEditProfile(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-[440px] max-w-[92vw] p-4" onMouseDown={(e) => e.stopPropagation()}>
            <div className="text-sm font-semibold text-gray-800 mb-3">{editProfile.id ? t('datadef:sam.editProfile') : t('datadef:sam.newProfile')}</div>
            <label className="block text-xs text-gray-500 mb-1">{t('common:ui.name')}</label>
            <input className={`${inp} w-full mb-2`} value={editProfile.name || ''} autoFocus placeholder={t('datadef:sam.profileNamePh')}
              onChange={(e) => setEditProfile({ ...editProfile, name: e.target.value })} />
            <div className="flex items-center gap-3 text-xs text-gray-700 mb-2">
              <label className="flex items-center gap-1"><input type="radio" checked={editProfile.format !== 'fixed'} onChange={() => setEditProfile({ ...editProfile, format: 'delimited' })} />{t('datadef:sam.delimited')}</label>
              <label className="flex items-center gap-1"><input type="radio" checked={editProfile.format === 'fixed'} onChange={() => setEditProfile({ ...editProfile, format: 'fixed' })} />{t('datadef:sam.fixed')}</label>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-700 mb-2">
              {editProfile.format !== 'fixed' && (
                <label className="flex items-center gap-1">{t('datadef:sam.delimiter')}
                  <input className={`${inp} w-12 text-center`} value={editProfile.delimiter || ''} onChange={(e) => setEditProfile({ ...editProfile, delimiter: e.target.value })} />
                </label>
              )}
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={editProfile.hasHeader !== false} onChange={(e) => setEditProfile({ ...editProfile, hasHeader: e.target.checked })} />{t('datadef:sam.headerYes')}
              </label>
              <label className="flex items-center gap-1">{t('datadef:sam.encoding')}
                <select className={inp} value={editProfile.encoding || 'UTF-8'} onChange={(e) => setEditProfile({ ...editProfile, encoding: e.target.value })}>
                  {['UTF-8', 'UTF-8-SIG', 'CP949', 'EUC-KR', 'UTF-16', 'LATIN-1'].map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>
            </div>
            <label className="block text-xs text-gray-500 mb-1">{t('common:ui.description')}</label>
            <input className={`${inp} w-full mb-3`} value={editProfile.description || ''} onChange={(e) => setEditProfile({ ...editProfile, description: e.target.value })} />
            <div className="flex justify-end gap-2">
              <button className="px-3 py-1 text-xs rounded border border-gray-300" onClick={() => setEditProfile(null)}>{t('common:ui.cancel')}</button>
              <button className="px-3 py-1 text-xs rounded bg-blue-600 text-white disabled:opacity-50" disabled={busy || !(editProfile.name || '').trim()}
                onClick={async () => { if (await call('/api/sam-types/profiles', 'POST', editProfile)) setEditProfile(null); }}>{t('dialog:vars.save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* SAM유형 편집 */}
      {editType && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-40" onMouseDown={() => setEditType(null)}>
          <div className="bg-white rounded-lg shadow-2xl w-[560px] max-w-[92vw] max-h-[84vh] overflow-auto p-4" onMouseDown={(e) => e.stopPropagation()}>
            <div className="text-sm font-semibold text-gray-800 mb-3">{editType.id ? t('datadef:sam.editSamType') : t('datadef:sam.newSamType')}</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('common:ui.name')}</label>
                <input className={`${inp} w-full`} value={editType.name || ''} autoFocus placeholder={t('datadef:sam.typeNamePh')}
                  onChange={(e) => setEditType({ ...editType, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('datadef:sam.profile')}</label>
                <select className={`${inp} w-full`} value={editType.profileId || ''} onChange={(e) => setEditType({ ...editType, profileId: e.target.value })}>
                  {profiles.map((p) => <option key={p.id} value={p.id}>{p.name} ({fmtOf(p)})</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('datadef:sam.pathPattern')}</label>
                <input className={`${inp} w-full font-mono`} value={editType.pathPattern || ''} placeholder={t('datadef:sam.pathPatternPh')}
                  onChange={(e) => setEditType({ ...editType, pathPattern: e.target.value })} />
              </div>
              {profiles.find((p) => p.id === editType.profileId)?.format === 'fixed' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{t('datadef:sam.fieldWidths')}</label>
                  <input className={`${inp} w-full font-mono`} value={editType.fieldWidths || ''} placeholder={t('datadef:sam.fieldWidthsPh')}
                    onChange={(e) => setEditType({ ...editType, fieldWidths: e.target.value })} />
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">{t('datadef:sam.layout')}</span>
              <button className="px-2 py-0.5 text-[0.6875rem] rounded border border-gray-300 hover:bg-gray-100"
                onClick={() => setEditType({ ...editType, columns: [...(editType.columns || []), { name: '', dataType: 'VARCHAR' } as DataDefinitionColumn] })}>{t('datadef:sam.addField')}</button>
            </div>
            <div className="border border-gray-200 rounded mb-3 max-h-52 overflow-auto">
              {(editType.columns || []).map((c, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2 py-1 border-b border-gray-100">
                  <span className="text-[0.6875rem] text-gray-400 w-5 text-right">{i + 1}</span>
                  <input className={`${inp} flex-1`} value={c.name} placeholder={t('datadef:sam.fieldName')}
                    onChange={(e) => { const cols = [...(editType.columns || [])]; cols[i] = { ...cols[i], name: e.target.value }; setEditType({ ...editType, columns: cols }); }} />
                  <select className={inp} value={c.dataType}
                    onChange={(e) => { const cols = [...(editType.columns || [])]; cols[i] = { ...cols[i], dataType: e.target.value }; setEditType({ ...editType, columns: cols }); }}>
                    {['VARCHAR', 'INTEGER', 'NUMERIC', 'DATE', 'TIMESTAMP'].map((d) => <option key={d}>{d}</option>)}
                  </select>
                  <button className="px-1 text-gray-400 hover:text-red-500"
                    onClick={() => setEditType({ ...editType, columns: (editType.columns || []).filter((_, j) => j !== i) })}>✕</button>
                </div>
              ))}
              {(editType.columns || []).length === 0 && <div className="p-2 text-xs text-gray-400">{t('datadef:sam.noFields')}</div>}
            </div>
            {editType.id && refCount(editType.id) > 0 && (
              <div className="mb-2 text-[0.6875rem] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                {t('datadef:sam.propagate', { n: refCount(editType.id) })}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button className="px-3 py-1 text-xs rounded border border-gray-300" onClick={() => setEditType(null)}>{t('common:ui.cancel')}</button>
              <button className="px-3 py-1 text-xs rounded bg-blue-600 text-white disabled:opacity-50"
                disabled={busy || !(editType.name || '').trim() || !editType.profileId}
                onClick={async () => { if (await call('/api/sam-types/types', 'POST', editType)) setEditType(null); }}>{t('dialog:vars.save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SamTypeManagerDialog;
